import type {
  AuditQuery,
  CapaActionListFilter,
  CircularListFilter,
  DocumentListFilter,
  MeetingListFilter,
  NonconformityListFilter,
  SgqIndicatorsQuery,
  SupportTypeListFilter,
} from '@qualicloud/contracts';

/**
 * Chaves de cache do TanStack Query.
 *
 * Convenção: `[domínio, recurso, ...discriminantes]`, sempre do mais geral
 * para o mais específico. Isso é o que torna a invalidação previsível — depois
 * de um ato ASSINADO, invalidamos `queryKeys.documents.all()` e todas as listas
 * e detalhes caem juntos, sem precisar enumerar cada filtro ativo.
 *
 * Por que isso importa aqui: um documento aprovado muda de situação em cinco
 * lugares ao mesmo tempo (Lista Mestra, detalhe, fila de aprovação, dashboard,
 * trilha). Cache desatualizado depois de uma assinatura mostraria ao usuário um
 * estado que não existe mais — e num sistema regulado isso é erro de registro.
 */
export const queryKeys = {
  session: {
    profile: () => ['session', 'profile'] as const,
  },

  documents: {
    all: () => ['documents'] as const,
    list: (filter: Partial<DocumentListFilter>) => ['documents', 'list', filter] as const,
    /** Contadores por situação da Lista Mestra — ged.md §14.4. */
    counts: (filter: Record<string, unknown>) => ['documents', 'counts', filter] as const,
    detail: (documentId: string) => ['documents', 'detail', documentId] as const,
    /** Agregado do BFF: detalhe + versões + manifesto + ciências numa chamada. */
    full: (documentId: string) => ['documents', 'full', documentId] as const,
    /** Listagem administrativa (ignora a ACL) — ged.md §14.3. */
    admin: (filter: Record<string, unknown>) => ['documents', 'admin', filter] as const,
    manifest: (recordType: string, recordId: string) =>
      ['documents', 'manifest', recordType, recordId] as const,
    pendingApprovals: () => ['documents', 'pending-approvals'] as const,
    pendingAcknowledgements: () => ['documents', 'pending-acknowledgements'] as const,
  },

  documentTypes: {
    all: () => ['document-types'] as const,
    tree: () => ['document-types', 'tree'] as const,
    detail: (id: string) => ['document-types', 'detail', id] as const,
  },

  dashboard: {
    all: () => ['dashboard'] as const,
    summary: (documentTypeId?: string) => ['dashboard', 'summary', documentTypeId ?? null] as const,
    myTasks: () => ['dashboard', 'my-tasks'] as const,
  },

  users: {
    all: () => ['users'] as const,
    list: (filter: Record<string, unknown>) => ['users', 'list', filter] as const,
    detail: (userId: string) => ['users', 'detail', userId] as const,
  },

  directory: {
    all: () => ['directory'] as const,
  },
  departments: {
    all: () => ['departments'] as const,
    tree: () => ['departments', 'tree'] as const,
    detail: (departmentId: string) => ['departments', 'detail', departmentId] as const,
  },

  organization: {
    all: () => ['organization'] as const,
    detail: (organizationId: string) => ['organization', 'detail', organizationId] as const,
    vendorAccess: () => ['organization', 'vendor-access'] as const,
  },

  audit: {
    all: () => ['audit'] as const,
    events: (query: Partial<AuditQuery>) => ['audit', 'events', query] as const,
    chainStatus: () => ['audit', 'chain-status'] as const,
  },

  notifications: {
    all: () => ['notifications'] as const,
    inbox: (page: number, onlyUnread: boolean) =>
      ['notifications', 'inbox', page, onlyUnread] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
    preferences: () => ['notifications', 'preferences'] as const,
  },

  // ── SGQ — Onda 2 ───────────────────────────────────────────────────────────

  nonconformities: {
    all: () => ['nonconformities'] as const,
    list: (filter: Partial<NonconformityListFilter>) =>
      ['nonconformities', 'list', filter] as const,
    detail: (nonconformityId: string) => ['nonconformities', 'detail', nonconformityId] as const,
  },

  /**
   * Catálogos administráveis do SGQ.
   *
   * `all()` sem parâmetro é a chave da lista padrão E o PREFIXO de invalidação:
   * `['meeting-types']` casa também com `['meeting-types', { includeInactive:
   * true }]`, então uma única invalidação atualiza o combo do formulário e a
   * grade de administração de uma vez.
   *
   * ⚠️ `usageCount` muda fora destas telas. Criar ou excluir uma reunião altera
   * a contagem do tipo, e o mesmo vale para solicitação — por isso as mutações
   * de reunião e de solicitação devem invalidar o catálogo correspondente. Sem
   * isso, a tela de administração diz "0 reuniões usam este tipo", oferece
   * "Excluir", e o servidor recusa com 409: o usuário descobre pelo erro o que
   * a tela deveria ter dito antes.
   */
  meetingTypes: {
    all: (params?: Partial<SupportTypeListFilter>): readonly unknown[] =>
      params === undefined ? ['meeting-types'] : ['meeting-types', params],
  },

  requestTypes: {
    all: (params?: Partial<SupportTypeListFilter>): readonly unknown[] =>
      params === undefined ? ['request-types'] : ['request-types', params],
  },

  meetings: {
    all: () => ['meetings'] as const,
    list: (filter: Partial<MeetingListFilter>) => ['meetings', 'list', filter] as const,
    detail: (meetingId: string) => ['meetings', 'detail', meetingId] as const,
  },

  capa: {
    all: () => ['capa'] as const,
    case: (capaCaseId: string) => ['capa', 'case', capaCaseId] as const,
    actions: (filter: Partial<CapaActionListFilter>) => ['capa', 'actions', filter] as const,
    action: (actionId: string) => ['capa', 'action', actionId] as const,
  },

  circulars: {
    all: () => ['circulars'] as const,
    list: (filter: Partial<CircularListFilter>) => ['circulars', 'list', filter] as const,
    detail: (circularId: string) => ['circulars', 'detail', circularId] as const,
  },

  sgqIndicators: {
    all: () => ['sgq-indicators'] as const,
    query: (query: Partial<SgqIndicatorsQuery>) => ['sgq-indicators', 'query', query] as const,
  },
} as const;

/**
 * Conjunto de chaves a invalidar depois de um ato ASSINADO sobre documento.
 *
 * Um ato assinado (§11.50) muda status regulatório: além do documento em si,
 * mexe na fila de aprovação, nos contadores do dashboard e na trilha. Manter
 * esta lista num único lugar impede o erro clássico de invalidar só o detalhe e
 * deixar a Lista Mestra mentindo.
 */
export function signedActInvalidations(documentId?: string): ReadonlyArray<readonly unknown[]> {
  const keys: Array<readonly unknown[]> = [
    queryKeys.documents.all(),
    queryKeys.dashboard.all(),
    queryKeys.audit.all(),
    queryKeys.notifications.all(),
  ];
  if (documentId) keys.push(queryKeys.documents.detail(documentId));
  return keys;
}

/**
 * Conjunto de chaves a invalidar depois de um ato ASSINADO do SGQ.
 *
 * O ciclo NC → SACAP → Ishikawa → 5W2H → eficácia → circular é AMARRADO: um
 * único ato mexe em vários agregados ao mesmo tempo, e invalidar só o que está
 * na tela deixa o resto do app mentindo. Três exemplos reais desta onda:
 *
 *  · avaliar a NC como PROCEDENTE cria o caso CAPA e muda o painel;
 *  · REPROVAR a eficácia de uma ação cria uma SACAP de follow-up em D+3, move a
 *    ação para ela e reabre a ação — a lista de reuniões, a lista de ações, o
 *    detalhe da NC e os indicadores mudam de uma vez;
 *  · encerrar a reunião SACAP muda o estado da NC vinculada.
 *
 * Por isso a invalidação é do DOMÍNIO INTEIRO, e não de uma chave pontual. É a
 * mesma decisão de `signedActInvalidations`, pelo mesmo motivo: cache
 * desatualizado depois de assinatura, em sistema regulado, é erro de registro.
 */
export function sgqSignedActInvalidations(): ReadonlyArray<readonly unknown[]> {
  return [
    queryKeys.nonconformities.all(),
    queryKeys.meetings.all(),
    // Reprovar a eficácia CRIA uma reunião de follow-up em D+3 — e criar
    // reunião muda o `usageCount` do tipo. Sem esta entrada, a tela de
    // administração de tipos continuaria dizendo que o tipo não é usado por
    // ninguém, e ofereceria uma exclusão que o servidor recusa.
    queryKeys.meetingTypes.all(),
    queryKeys.capa.all(),
    queryKeys.circulars.all(),
    queryKeys.sgqIndicators.all(),
    queryKeys.dashboard.all(),
    queryKeys.audit.all(),
    queryKeys.notifications.all(),
  ];
}
